import {DataSource} from '@angular/cdk/table';
import {CollectionViewer} from '@angular/cdk/collections';
import {BehaviorSubject, merge, Observable, of, Subject, throwError} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  takeUntil,
  tap
} from 'rxjs/operators';
import { PaymentListItemReadDto } from '../../models/payment/payment-list-item-read-dto.model';
import { PaymentSearchOptionsDto } from '../paymentSearchOptionsDto.model';
import { SearchOptionsResponse } from 'src/app/shared/models/SearchOptionsResponse.model';
import { UnitRecipientPaymentsHistoryTableService } from './unit-recipient-payments-history-table.service';
import { UnitPaymentsDataService } from 'src/app/services/shared/unit-payments-data.service';



export class UnitRecipientPaymentsHistoryDataSource extends DataSource<PaymentListItemReadDto> {
  private destroy$ = new Subject();

  // Triggers for refreshing data
  private searchOptions$ = this.paymentsHistoryTableService.searchOptions$;
  private refreshData$ = this.paymentsHistoryTableService.refreshData$.pipe(tap(_ => this.paymentsHistoryTableService.updatePageNumber(0)));

  private _totalRowsCount$ = new BehaviorSubject<number>(0);
  public totalRowsCount$ = this._totalRowsCount$.pipe(takeUntil(this.destroy$), distinctUntilChanged(), shareReplay(1));

  // For a loading indicator
  private _loading$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._loading$.pipe(takeUntil(this.destroy$), shareReplay(1));

  public pageRows$ = merge(this.refreshData$, this.searchOptions$)
    .pipe(
      switchMapTo(this.searchOptions$),
      takeUntil(this.destroy$),
      filter(searchOptions => {
        return searchOptions !== null && !!searchOptions.recipientId;
      }),
      tap(() => this._loading$.next(true)),
      switchMap(searchOptions => this.getPaymentsHistory(searchOptions)),
      tap(response => {
        this._totalRowsCount$.next(response.totalCount);
      }),
      map(response => response.rows),
      tap(() => this._loading$.next(false)),
      shareReplay(1)
    );

  private getPaymentsHistory(searchOptions: PaymentSearchOptionsDto): Observable<SearchOptionsResponse<PaymentListItemReadDto>> {
    return this.crePaymentsDataService.paymentsHistory(searchOptions).pipe(
      catchError(err => {
        return of(new SearchOptionsResponse<PaymentListItemReadDto>());
      })
    );
  }

  connect(collectionViewer: CollectionViewer): Observable<PaymentListItemReadDto[] | ReadonlyArray<PaymentListItemReadDto>> {
    this._loading$.next(true);
    return this.pageRows$.pipe(
      tap((rows) => this._loading$.next(false)),
      catchError((error) => {
        this._loading$.next(false);
        return throwError(error);
      })
    );
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }


  constructor(private paymentsHistoryTableService: UnitRecipientPaymentsHistoryTableService,
              private crePaymentsDataService: UnitPaymentsDataService) {
    super();
  }
}
